"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

// Color palette for avatars based on first letter
const colorPalettes: Record<string, {
  bg: string;
  border: string;
  textColor: string;
}> = {
  A: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  B: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
  C: { bg: 'bg-avatar-purple/15', border: 'border-avatar-purple/25', textColor: 'text-avatar-purple' },
  D: { bg: 'bg-avatar-yellow/15', border: 'border-avatar-yellow/25', textColor: 'text-avatar-yellow' },
  E: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  F: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
  G: { bg: 'bg-avatar-purple/15', border: 'border-avatar-purple/25', textColor: 'text-avatar-purple' },
  H: { bg: 'bg-avatar-yellow/15', border: 'border-avatar-yellow/25', textColor: 'text-avatar-yellow' },
  I: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  J: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
  K: { bg: 'bg-avatar-purple/15', border: 'border-avatar-purple/25', textColor: 'text-avatar-purple' },
  L: { bg: 'bg-avatar-yellow/15', border: 'border-avatar-yellow/25', textColor: 'text-avatar-yellow' },
  M: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  N: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
  O: { bg: 'bg-avatar-purple/15', border: 'border-avatar-purple/25', textColor: 'text-avatar-purple' },
  P: { bg: 'bg-avatar-yellow/15', border: 'border-avatar-yellow/25', textColor: 'text-avatar-yellow' },
  Q: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  R: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
  S: { bg: 'bg-avatar-purple/15', border: 'border-avatar-purple/25', textColor: 'text-avatar-purple' },
  T: { bg: 'bg-avatar-yellow/15', border: 'border-avatar-yellow/25', textColor: 'text-avatar-yellow' },
  U: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  V: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
  W: { bg: 'bg-avatar-purple/15', border: 'border-avatar-purple/25', textColor: 'text-avatar-purple' },
  X: { bg: 'bg-avatar-yellow/15', border: 'border-avatar-yellow/25', textColor: 'text-avatar-yellow' },
  Y: { bg: 'bg-avatar-blue/15', border: 'border-avatar-blue/25', textColor: 'text-avatar-blue' },
  Z: { bg: 'bg-avatar-green/15', border: 'border-avatar-green/25', textColor: 'text-avatar-green' },
};

const defaultPalette = {
  bg: 'bg-avatar-gray/20',
  border: 'border-avatar-gray/35',
  textColor: 'text-gray-600',
};

interface UserAvatarProps {
  name?: string | null;
  email?: string;
  avatarUrl?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showGlow?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

const iconSizeClasses = {
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-xl',
  xl: 'rounded-2xl',
};

export function UserAvatar({ 
  name, 
  email, 
  avatarUrl, 
  size = 'md',
  showGlow = false,
  className 
}: UserAvatarProps) {
  const displayName = name || email || '?';
  const initial = displayName.charAt(0).toUpperCase();
  const palette = colorPalettes[initial] || defaultPalette;

  if (avatarUrl) {
    return (
      <div className={cn("relative", className)}>
        {showGlow && (
          <div className={cn(
            'absolute inset-0 blur-lg scale-110',
            palette.bg
          )} />
        )}
        <img 
          src={avatarUrl} 
          alt={displayName} 
          className={cn(
            'relative object-cover border',
            sizeClasses[size],
            iconSizeClasses[size],
            palette.border
          )}
        />
      </div>
    );
  }

  return (
    <div className={cn("relative group", className)}>
      {showGlow && (
        <div className={cn(
          'absolute inset-0 blur-lg scale-110 opacity-60',
          palette.bg
        )} />
      )}
      <div 
        className={cn(
          'relative flex items-center justify-center border font-semibold',
          sizeClasses[size],
          iconSizeClasses[size],
          palette.bg,
          palette.border,
          palette.textColor
        )}
      >
        {initial}
      </div>
    </div>
  );
}
